import { useSignal } from '@preact/signals';
import { useCallback, useRef, useEffect, useMemo } from 'preact/hooks';
import { UAParser } from 'ua-parser-js';
import { localize, pluralize } from 'i18n/localize.ts';
import { cn } from 'utils/cn.tsx';
import { Icon } from 'components/atoms/Icon.tsx';
import { isOfferAbleToTop, type OfferData, type WhereSold } from 'service/offer/model.ts';
import { Dropdown, type ImperativeHandle } from 'components/dropdown/Dropdown.tsx';
import { CommonPortalContainer } from 'components/dropdown/containers/CommonPortalContainer.tsx';
import { type WithdrawOptionId, withdrawOptions, renderIconOption } from 'components/offer/WithdrawOptions';
import { type IconOptionType } from 'components/dropdown/options/IconOption';
import { sendEditOfferAnalytics } from 'lib/dot/seller/utils.ts';

const $t = localize();

const texts = {
	reserve: $t.offer.reserveAction,
	unreserve: $t.offer.unreserveAction,
	delete: $t.offer.deleteAction,
	edit: $t.offer.editAction,
	publish: $t.offer.publishAction,
	withdraw: $t.offer.withdrawAction,
	top: $t.offer.topAction,
};

const icons = {
	toggleReserve: 'flag',
	toggleUnreserve: 'flag_check',
	delete: 'delete',
	edit: 'stylus',
	publish: 'visibility',
	withdraw: 'visibility_off',
	top: 'star',
} as const;

function getOptionText(option: Option, offer: Partial<OfferData>) {
	switch (option) {
		case 'toggleVisibility':
			return offer.status === 'active' ? texts.withdraw : texts.publish;
		case 'toggleReserve':
			return offer.isReserved ? texts.unreserve : texts.reserve;
		default:
			return texts[option];
	}
}

function getIconType(option: Option, offer: Partial<OfferData>) {
	switch (option) {
		case 'toggleVisibility':
			return offer.status === 'active' ? icons.withdraw : icons.publish;
		case 'toggleReserve':
			return offer.isReserved ? icons.toggleUnreserve : icons.toggleReserve;
	}
	return icons[option];
}

function getIcon(option: Option, offer: Partial<OfferData>) {
	const type = getIconType(option, offer);
	return (
		<Icon
			className="text-dark-blue-25 group-hover:text-dark-blue group-focus:text-dark-blue transition-colors"
			type={type}
			fill
			size={24}
		/>
	);
}

function getUrl(option: Option, offer: Partial<OfferData>) {
	if (option === 'edit') {
		return `/inzerat/${offer.seoName}/editace`;
	}
	if (option === 'top') {
		return `/inzerat/${offer.seoName}/topovani`;
	}
	return null;
}

const OPTIONS = ['edit', 'top', 'toggleVisibility', 'toggleReserve', 'delete'] as const;

type Option = (typeof OPTIONS)[number];

export type Props = {
	voucherCount?: number;
	offer: OfferData;
	openerClassName?: string;
	renderOpener?: (
		offer: Partial<OfferData>,
		forceTransparentOpener: boolean,
		className: undefined | string,
		{ toggleOpen, openerRef, aria, isOpen, onKeyDown }: Dropdown.RenderOpenerProps,
	) => JSX.Element;
	onOfferDelete?: (event: Event, offer: OfferData, reason: WhereSold) => void;
	onOfferToggleVisibility?: (event: Event, offer: OfferData, reason: WhereSold) => void;
	onOfferReserve?: (event: Event, offer: OfferData) => void;
	onDropdownOpen?: () => void;
	onDropdownClose?: () => void;
	forceTransparentOpener?: boolean;
};

export function OfferOptions({
	forceTransparentOpener,
	voucherCount,
	offer,
	openerClassName,
	renderOpener = renderButtonOpener,
	onOfferDelete,
	onOfferToggleVisibility,
	onOfferReserve,
	onDropdownOpen,
	onDropdownClose,
}: Props) {
	const dropdownPortalRef = useRef<HTMLDivElement>(null);
	const dropdownPlacement = useSignal<Dropdown.Placement>('bottom-right');
	const secondLevelWithdrawDropdownRef = useRef<HTMLDivElement>(null);
	const secondLevelDeleteDropdownRef = useRef<HTMLDivElement>(null);
	const isSecondLevelOpen = useSignal<boolean>(false);

	// Safari 18.0 and lower has different behavior of container query positioning https://frontendmasters.com/blog/containers-context/
	// https://developer.apple.com/documentation/safari-release-notes/safari-18_1-release-notes#CSS
	// We will update dropdown placement to be inside of the offer card container.
	useEffect(() => {
		const parser = new UAParser();
		const browser = parser.getBrowser();
		if (!browser.name?.includes('Safari')) {
			return;
		}

		const version = parseFloat(browser.version ?? '0');
		if (version < 18.1) {
			dropdownPlacement.value = 'bottom-left';
		}
	}, []);

	const renderCommonContainerWithClass = useCallback(
		(containerProps: Dropdown.RenderContainerProps) => (
			<CommonPortalContainer
				{...containerProps}
				className={cn(
					'px-6 py-8 md:p-4 md:py-2 md:w-[242px]',
					containerProps.isOpen && isSecondLevelOpen.value && 'hidden md:block',
				)}
			/>
		),
		[],
	);
	const renderSubContainerWithClass = useCallback(
		(containerProps: Dropdown.RenderContainerProps, dropdownClassName: string) => (
			<CommonPortalContainer
				{...containerProps}
				className={cn(
					'px-6 py-8 md:p-4 md:py-2 md:w-[204px]',
					containerProps.placement === 'bottom-right' && 'md:ml-[250px]',
					containerProps.placement === 'bottom-left' && 'md:mr-[48px]',
					dropdownClassName,
				)}
			/>
		),
		[],
	);

	const onOptionSelected = useCallback<Dropdown.OptionSelectedHandler<Option>>(
		(event, newValue) => {
			event.stopPropagation();

			if (newValue === 'delete') {
				onOfferDelete?.(event, offer, 'not_specified');
			} else if (newValue === 'toggleReserve') {
				onOfferReserve?.(event, offer);
			} else if (newValue === 'toggleVisibility') {
				onOfferToggleVisibility?.(event, offer, 'not_specified');
			}
		},
		[onOfferDelete, onOfferReserve, onOfferToggleVisibility],
	);

	const onWithdrawOptionSelected = useCallback<Dropdown.OptionSelectedHandler<IconOptionType<WithdrawOptionId>>>(
		(event, newValue) => {
			event.stopPropagation();
			if (!newValue) {
				return;
			}

			if (newValue.id === 'soldOnSbazar') {
				onOfferToggleVisibility?.(event, offer, 'sold_sbazar');
			} else if (newValue.id === 'soldElsewhere') {
				onOfferToggleVisibility?.(event, offer, 'sold_other');
			} else if (newValue.id === 'soldNoInfo') {
				onOfferToggleVisibility?.(event, offer, 'not_specified');
			}
		},
		[onOfferToggleVisibility],
	);

	const onDeleteOptionSelected = useCallback<Dropdown.OptionSelectedHandler<IconOptionType<WithdrawOptionId>>>(
		(event, newValue) => {
			event.stopPropagation();

			if (newValue.id === 'soldOnSbazar') {
				onOfferDelete?.(event, offer, 'sold_sbazar');
			} else if (newValue.id === 'soldElsewhere') {
				onOfferDelete?.(event, offer, 'sold_other');
			} else if (newValue.id === 'soldNoInfo') {
				onOfferDelete?.(event, offer, 'not_specified');
			}
		},
		[onOfferDelete],
	);

	const outsideClickRefs = useMemo(() => {
		return [secondLevelWithdrawDropdownRef, secondLevelDeleteDropdownRef];
	}, []);

	const renderOption = useCallback(
		({ option, props, aria, id }: Dropdown.RenderOptionProps<(typeof OPTIONS)[number]>) => {
			if (offer.approvalAdmin === 'rejected' && ['top', 'toggleVisibility', 'toggleReserve'].includes(option)) {
				return null;
			}

			if ((option === 'toggleVisibility' && offer.status === 'active') || option === 'delete') {
				const title = getOptionText(option, offer);
				const onOptionSelected =
					option === 'toggleVisibility' ? onWithdrawOptionSelected : onDeleteOptionSelected;
				const dataTest = option === 'toggleVisibility' ? 'withdraw-container' : 'delete-container';
				const dropdownClassName = option === 'toggleVisibility' ? 'md:mt-[50px]' : 'md:mt-[92px]';
				return (
					<Dropdown
						containerPortalRefElement={dropdownPortalRef}
						wrapperPortalRefElement={
							option === 'toggleVisibility'
								? secondLevelWithdrawDropdownRef
								: secondLevelDeleteDropdownRef
						}
						renderContainer={(props) => renderSubContainerWithClass(props, dropdownClassName)}
						placement={dropdownPlacement.value}
						renderOpener={({ aria, toggleOpen, openerRef, onKeyDown }) => (
							<button
								title={title}
								class="group flex items-center justify-between w-full text-left py-2 text-dark-blue cursor-pointer text-sm"
								ref={openerRef}
								onClick={toggleOpen}
								onKeyDown={onKeyDown}
								{...aria}
							>
								<span class="flex items-center gap-2">
									{getIcon(option, offer)}
									<div>{title}</div>
								</span>{' '}
								<Icon
									type="arrow_forward_ios"
									size={16}
									className="text-dark-blue-25 group-hover:text-dark-blue group-focus:text-dark-blue transition-colors"
								/>
							</button>
						)}
						options={withdrawOptions}
						renderOption={renderIconOption}
						onOptionSelected={onOptionSelected}
						mobileTitle={title}
						dataTest={dataTest}
						onOpen={() => {
							isSecondLevelOpen.value = true;
						}}
						onClose={() => {
							isSecondLevelOpen.value = false;
							rootDropdownRef.current?.close();
						}}
					/>
				);
			}

			const url = getUrl(option, offer);
			const Tag = url ? 'a' : 'button';
			return (
				<Tag
					class="group flex items-center gap-2 w-full text-left py-2 text-dark-blue cursor-pointer text-sm"
					{...props}
					{...aria}
					{...(url && { href: url })}
					id={id}
					data-test={option}
					onClick={(e) => {
						if (option === 'edit' && offer.id) {
							sendEditOfferAnalytics(offer.id);
						}
						props.onClick(e);
					}}
				>
					{getIcon(option, offer)}
					<span>{getOptionText(option, offer)}</span>
					{option === 'top' && !!voucherCount && voucherCount >= 1 && (
						<span class="text-dark-blue-50 md:hidden">
							{' '}
							({pluralize(voucherCount, $t.offerAdministration.voucherCount, true)})
						</span>
					)}
				</Tag>
			);
		},
		[offer, voucherCount],
	);

	const onClose = useCallback(() => {
		isSecondLevelOpen.value = false;
		onDropdownClose?.();
	}, [onDropdownClose]);

	const rootDropdownRef = useRef<ImperativeHandle | null>(null);

	return (
		<div className="relative" ref={dropdownPortalRef}>
			<Dropdown
				className="text-dark-blue"
				options={getOptions(offer)}
				placement={dropdownPlacement.value}
				renderContainer={renderCommonContainerWithClass}
				renderOpener={(openerOptions) =>
					renderOpener(offer, !!forceTransparentOpener, openerClassName, openerOptions)
				}
				renderOption={renderOption}
				onOptionSelected={onOptionSelected}
				onOpen={onDropdownOpen}
				onClose={onClose}
				outsideClickRef={outsideClickRefs}
				ref={rootDropdownRef}
			></Dropdown>
		</div>
	);
}

function getOptions(offer: Partial<OfferData>) {
	if (!isOfferAbleToTop(offer)) {
		return OPTIONS.filter((option) => option !== 'top');
	}
	return OPTIONS;
}

function renderButtonOpener(
	offer: Partial<OfferData>,
	forceTransparentOpener: boolean,
	className: undefined | string,
	{ toggleOpen, openerRef, aria, isOpen, onKeyDown }: Dropdown.RenderOpenerProps,
) {
	const hasProcessingVideo = offer.videos?.some((video) => video.status === 'processing');
	const invertedBg = !offer.images?.length || hasProcessingVideo;
	return (
		<button
			title={$t.offer.moreAction}
			class={cn(
				'flex items-center justify-center size-10 rounded-lg',
				!invertedBg && 'bg-white text-dark-blue-50 hover:text-dark-blue',
				!invertedBg && isOpen && 'text-dark-blue',
				invertedBg && 'bg-dark-blue-50 text-white hover:bg-dark-blue',
				invertedBg && isOpen && 'bg-dark-blue',
				forceTransparentOpener && 'bg-transparent hover:bg-transparent text-dark-blue-50 hover:text-dark-blue',
				className,
			)}
			ref={openerRef}
			{...aria}
			aria-label={$t.offer.moreAction}
			onClick={(e) => {
				toggleOpen();
				e.preventDefault();
			}}
			onKeyDown={onKeyDown}
		>
			<Icon type="more_horiz" size={24} />
		</button>
	);
}
